import { useMemo, useState } from "react";
import { Package, Plus, X, Search } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useToast } from "@/hooks/use-toast";
import {
  useCommodities,
  useInsuredCommodities,
  useAddInsuredCommodity,
  useRemoveInsuredCommodity,
} from "@/lib/api";

// CSR-1658 — Assets-tab Commodities section. Unlike Loss Payees/Waivers (full
// dialog + table) this is inline: a searchable combobox that picks an existing
// catalog commodity OR adds a new one, with selections shown as removable tags.
// Add/remove persist immediately. Global catalog is shared across all insureds.
export function CommoditiesSection({ insuredId }: { insuredId: string }) {
  const { toast } = useToast();
  const { data: links, isLoading } = useInsuredCommodities(insuredId);
  const { data: catalog } = useCommodities();
  const addMutation = useAddInsuredCommodity(insuredId);
  const removeMutation = useRemoveInsuredCommodity(insuredId);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const items = links ?? [];
  const linkedNames = useMemo(
    () => new Set(items.map((l) => l.name.trim().toLowerCase())),
    [items],
  );

  // Catalog entries not already linked to this insured, filtered by the query.
  const suggestions = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (catalog ?? [])
      .filter((c) => !linkedNames.has(c.name.trim().toLowerCase()))
      .filter((c) => (q ? c.name.toLowerCase().includes(q) : true));
  }, [catalog, linkedNames, search]);

  const trimmed = search.trim();
  const exactExists =
    !!trimmed &&
    (catalog ?? []).some(
      (c) => c.name.trim().toLowerCase() === trimmed.toLowerCase(),
    );
  const alreadyLinked = !!trimmed && linkedNames.has(trimmed.toLowerCase());

  const handleAdd = async (name: string) => {
    setOpen(false);
    setSearch("");
    try {
      await addMutation.mutateAsync(name);
    } catch (err) {
      toast({
        title: "Error",
        description: (err as Error).message,
        variant: "destructive",
      });
    }
  };

  const handleRemove = async (linkId: string) => {
    try {
      await removeMutation.mutateAsync(linkId);
    } catch (err) {
      toast({
        title: "Error",
        description: (err as Error).message,
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="overflow-hidden" data-testid="commodities-section">
      <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Package className="w-5 h-5 text-foreground" aria-hidden="true" />
          <h3 className="text-base font-semibold">Commodities</h3>
        </div>
        <div className="flex items-center gap-3">
          <span
            className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-2 rounded-full bg-muted text-xs font-medium text-muted-foreground"
            data-testid="commodities-count"
          >
            {items.length}
          </span>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                data-testid="commodities-add"
              >
                <Plus className="w-4 h-4" /> Add
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-72 rounded-xl" align="end">
              <Command shouldFilter={false}>
                <div className="flex items-center border-b px-3">
                  <Search className="w-4 h-4 mr-2 opacity-50 shrink-0" />
                  <CommandInput
                    value={search}
                    onValueChange={setSearch}
                    placeholder="Search or add commodity..."
                    className="h-10 border-0 focus:ring-0"
                  />
                </div>
                {/* CSR-1177: stop wheel/touch propagation so the list scrolls
                    when the Popover is portaled outside a modal subtree. */}
                <CommandList
                  className="max-h-64"
                  onWheel={(e) => e.stopPropagation()}
                  onTouchMove={(e) => e.stopPropagation()}
                >
                  <CommandEmpty>
                    {!trimmed ? (
                      <span className="block px-3 py-4 text-sm text-muted-foreground text-center">
                        Type to search or add a commodity.
                      </span>
                    ) : null}
                  </CommandEmpty>
                  <CommandGroup>
                    {suggestions.map((c) => (
                      <CommandItem
                        key={c.id}
                        value={c.name}
                        onSelect={() => handleAdd(c.name)}
                        className="data-[selected=true]:!bg-muted data-[selected=true]:!text-foreground"
                      >
                        {c.name}
                      </CommandItem>
                    ))}
                    {trimmed && !exactExists && !alreadyLinked && (
                      <CommandItem
                        value={`__add_${trimmed}`}
                        onSelect={() => handleAdd(trimmed)}
                        className="data-[selected=true]:!bg-muted data-[selected=true]:!text-foreground"
                      >
                        Add "<span className="font-medium ml-1">{trimmed}</span>"
                      </CommandItem>
                    )}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="px-6 py-4">
        <p className="text-sm text-muted-foreground mb-3">
          Commodities this customer hauls on a regular basis.
        </p>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">No commodities yet.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {items.map((link) => (
              <Badge
                key={link.id}
                variant="outline"
                className="gap-1 pl-2.5 pr-1 py-1 font-normal !bg-muted !border-transparent text-foreground"
                data-testid={`commodity-tag-${link.id}`}
              >
                {link.name}
                <button
                  type="button"
                  aria-label={`Remove ${link.name}`}
                  onClick={() => handleRemove(link.id)}
                  className="rounded-full hover:bg-muted-foreground/20 p-0.5 cursor-pointer"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
